import { HorizontalGaugeLevels, noticeError } from '@ratehub/base-ui';

import PRODUCT_TYPES from '../definitions/ProductTypes';
import CARD_ID_EXCEPTIONS from '../definitions/CardIdExceptions';


/**
 * Runs integrity checks on a credit card and returns whether it is valid.
 * Logs a noticeError with context and failed rule descriptions when any check fails.
 * @param {object} cardInfo - the credit card information being verified
 * @param {string} productType - the type of card used to determine product-specific rules
 * @returns {boolean} true if all validity tests passed; false otherwise (a noticeError is logged on failure)
 */
function verifyCardIntegrity(cardInfo, productType) {

    const COMMON_RULES = {
        ...notEmptyRules(CANNOT_BE_EMPTY['common'], cardInfo),

        // lets null be valid for an unspecified minimum credit score
        'minimum credit score must be >= 300': () => cardInfo.minimumCreditScore >= 300
            || cardInfo.minimumCreditScore === null,
    };

    const rules = {
        ...COMMON_RULES,
        ...getProductSpecificRules(productType, cardInfo),
    };

    const ruleEntries = Object.entries(rules);
    const failedRules = ruleEntries
        // eslint-disable-next-line no-unused-vars
        .filter(([ _, validityRule ]) => {
            try {
                return !validityRule();
            } catch {
                return true;
            }
        })
        .map(([ description ]) => description);

    if (failedRules.length) {
        noticeError(
            new Error(`[verifyCardIntegrity] Card validity check failed for “${cardInfo?.name}”`),
            {
                productType,
                cardId: cardInfo?.id,
                cardName: cardInfo?.name,
                slug: cardInfo?.slug,
                failedRules,
            },
        );
        return false;
    }

    return true;
}


function getProductSpecificRules(productType, cardInfo) {
    switch (productType) {
        case PRODUCT_TYPES.TRAVEL:
            return {
                ...travelRules(cardInfo),
                ...rewardProductRules(cardInfo),
            };

        case PRODUCT_TYPES.CASH_BACK:
            return {
                ...cashBackRules(cardInfo),
                ...rewardProductRules(cardInfo),
            };

        case PRODUCT_TYPES.STORE_CREDIT:
            return {
                ...rewardProductRules(cardInfo),
            };

        case PRODUCT_TYPES.ANY_REWARDS:
            return {
                // like Travel, but we don’t mind missing redemptionFlexibility information (so no travelRules())
                ...rewardProductRules(cardInfo),
            };

        case PRODUCT_TYPES.LOW_INTEREST:
            return lowInterestRules(cardInfo);

        case PRODUCT_TYPES.BALANCE_TRANSFER:
            return balanceTransferRules(cardInfo);

        case PRODUCT_TYPES.SECURED:
            return securedRules(cardInfo);

        default:
            noticeError(new RangeError(
                `[${cardInfo.id} • ${cardInfo.name}]\n`
                + `\t• unrecognized product type “${productType}” prevented checking for product-specific rules`),
            {
                productType: productType,
                cardId: cardInfo.id,
                cardName: cardInfo.name,
            });

            return {};
    }
}

/**
 * rules specific to the validity needs of a travel card
 * @param {object} card - the credit card whose data is being validated
 * @returns {object} an object like { 'rule description': validationFunction, …}
 */
function rewardProductRules(card) {
    return {
        ...notEmptyRules(CANNOT_BE_EMPTY[PRODUCT_TYPES.ANY_REWARDS], card),
        ...topLevelObjectsNotEmptyRules(REWARD_CARD_TOP_LEVEL_OBJECTS_THAT_CANNOT_BE_EMPTY, card),
        ...earningsDescriptionRules(card),
    };
}


/**
 * rules specific to the earningsDescription card structure
 * @param {object} card - the credit card whose data is being validated
 * @returns {object} an object like { 'rule description': validationFunction, …}
 */
function earningsDescriptionRules(card) {
    return {
        'earningsDescription array must not be empty': () =>
            Array.isArray(card.earningsDescription) && card.earningsDescription.length,

        'earningsDescription types must be an array':
            () => card.earningsDescription.every(description => Array.isArray(description.types)),

        ['earningsDescription rate must not be 0']:
            () => card.earningsDescription.every(
                description => {
                    return typeof (description.rate) === 'number' && description.rate !== 0;
                }),
    };
}


/**
 * rules specific to the validity needs of a travel card
 * @param {object} card - the credit card whose data is being validated
 * @returns {object} an object like { 'rule description': validationFunction, …}
 */
function travelRules(card) {
    // only one requirement as of now: redemptionFlexibility
    return {
        ...topLevelObjectsNotEmptyRules([
            [ 'redemptionFlexibility',
                [ 'level', 'description' ],
            ],
        ], card),
        'redemptionFlexibility level must be a HorizontalGaugeLevel':
            () => Object.values(HorizontalGaugeLevels).includes(card.redemptionFlexibility?.level),
    };
}

/**
 * rules specific to the validity needs of a Cash Back card
 * @param {object} card - the credit card whose data is being validated
 * @returns {object} an object like { 'rule description': validationFunction, …}
 */
function cashBackRules(card) {
    return {
        ...notEmptyRules(CANNOT_BE_EMPTY[PRODUCT_TYPES.CASH_BACK], card),
        ...earningsDescriptionRules(card),
    };
}


/**
 * rules specific to the validity needs of a low interest card
 * @param {object} card - the credit card whose data is being validated
 * @returns {object} an object like { 'rule description': validationFunction, …}
 */
function lowInterestRules(card) {
    return {
        ...notEmptyRules(CANNOT_BE_EMPTY[PRODUCT_TYPES.LOW_INTEREST], card),
        'purchaseInterest cannot be null for LI card':
            () => card.purchaseInterest !== null,
    };
}

/**
 * rules specific to the validity needs of a balance transfer card
 * @param {object} card - the credit card whose data is being validated
 * @returns {object} an object like { 'rule description': validationFunction, …}
 */
function balanceTransferRules(card) {
    return {
        ...notEmptyRules(CANNOT_BE_EMPTY[PRODUCT_TYPES.BALANCE_TRANSFER], card),
        ...numericNonZeroRules([ 'balanceTransferPromoMonths' ], card),
        'balanceTransferInterest cannot be null for BT card':
            () => card.balanceTransferInterest !== null,

        'cashAdvanceInterest is required if balanceTransferInterest is missing':
            () => typeof card.cashAdvanceInterest === 'number' || typeof card.balanceTransferInterest === 'number',
    };
}


/**
 * rules specific to the validity needs of a secured card
 * @param {object} card - the credit card whose data is being validated
 * @returns {object} an object like { 'rule description': validationFunction, …}
 */
function securedRules(card) {
    return {
        ...!CARD_ID_EXCEPTIONS.CapitalOneGuaranteed.includes(card.id)
            && numericNonZeroRules([ 'minimumDeposit' ], card),
        'purchaseInterest cannot be null for Secured card': () => card.purchaseInterest !== null,
    };
}

/**
 * validity rules for top-level properties that must not be empty
 * @param {string[]} propertyNames - array of top-level property names
 * @param {object} card - the credit card whose data is being validated
 * @returns {object} an object like { 'rule description': validationFunction, …}
 */
function notEmptyRules(propertyNames, card) {
    return propertyNames.reduce((rulesObject, propName) => ({
        ...rulesObject,
        [`${propName} must not be empty`]: () => card[propName] || card[propName] === 0,
    }), {});
}

/**
 * validity rules for top-level properties that must be non-zero numbers
 * @param {string[]} propertyNames - array of top-level property names
 * @param {object} card - the credit card whose data is being validated
 * @returns {object} an object like { 'rule description': validationFunction, …}
 */
function numericNonZeroRules(propertyNames, card) {
    return propertyNames.reduce((rulesObject, propName) => ({
        ...rulesObject,
        [`${propName} must not be zero`]: () => typeof card[propName] === 'number' && card[propName] !== 0,
    }), {});
}

/**
 * validity rules for top-level objects whose properties must not be empty
 * @param {Array<[string, string[]]>} objectNames - top-level object name and its properties that can’t be empty
 * @param {object} card - the credit card whose data is being validated
 * @returns {object} an object like { 'rule description': validationFunction, …}
 */
function topLevelObjectsNotEmptyRules(objectNames, card) {
    return objectNames.reduce((rulesObject, objNames) => {
        const topLevelObjectName = objNames[0];
        const subPropNames = objNames[1];

        return {
            ...rulesObject,
            [`${topLevelObjectName} object must not be empty`]:
                () => card[topLevelObjectName] && typeof card[topLevelObjectName] === 'object',
            ...nonEmptyObjectRules(topLevelObjectName, subPropNames, card),
        };
    }, {});
}

/**
 * validity rules for objects whose properties must not be empty
 * @param {string} parentObjName - top-level object name
 * @param {string[]} subPropertyNames - array of top-level object names
 * @param {object} card - the credit card whose data is being validated
 * @returns {object} an object like { 'rule description': validationFunction, …}
 */
function nonEmptyObjectRules(parentObjName, subPropertyNames, card) {
    return subPropertyNames.reduce((rulesObject, subPropName) => ({
        ...rulesObject,
        [`${parentObjName}.${subPropName} must not be empty`]:
            () => card[parentObjName][subPropName] || card[parentObjName][subPropName] === 0,
    }), {});
}


const CANNOT_BE_EMPTY = {
    common: [
        'id',
        'name',
        'cardImageURL',
        'detailsURL',
        'applyRedirectURL',

        // interest rates rules applied on a per-context basis
    ],

    [PRODUCT_TYPES.ANY_REWARDS]: [
        'totalSubsequentYearPoints',
        'netSubsequentYearValue',
        'welcomeBonus',

        'monthlySpendingAverage',
    ],

    [PRODUCT_TYPES.TRAVEL]: [
        'features',                         // a string array, but allowed to have no items
    ],

    [PRODUCT_TYPES.STORE_CREDIT]: [],

    [PRODUCT_TYPES.CASH_BACK]: [
        'howToRedeemDescription',
    ],

    [PRODUCT_TYPES.LOW_INTEREST]: [
        'lowInterestMonthlyBalance',
        'lowInterestAnnualInterestFee',
        'lowInterestMonthlyInterestFee',
    ],

    [PRODUCT_TYPES.BALANCE_TRANSFER]: [
        'balanceTransferPromoMonths',       // BT card without a promo period is nonsensical
        'balanceTransferPromoInterest',
    ],
};

const REWARD_CARD_TOP_LEVEL_OBJECTS_THAT_CANNOT_BE_EMPTY = [
    [ 'welcomeSignUp',
        [ 'points', 'value' ],
    ],
    [ 'welcomeSpending',
        [ 'points', 'value' ],
    ],
];

export default verifyCardIntegrity;
